class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.observeSelectedVariantAttribute();
  }

  connectedCallback() {
    this.elements = {
      prevButton: this.querySelector('.prev-button'),
      nextButton: this.querySelector('.next-button'),
    };

    this.options = {
      axis: 'x',
      container: '[data-track]',
      slides: '[data-slide]',
      loop: true,
    };

    this.option_thumbs = {
      containScroll: 'keepSnaps',
      dragFree: true,
    };

    this.viewportNodeMainCarousel = this.querySelector('.main-viewport');
    this.viewportNodeThumbCarousel = this.querySelector('.thumbnail-viewport');
    this.thumbStripNode = this.querySelector('.thumbnail-strip');

    this.init();
    this.handleInitialVariantSelection();
  }

  observeSelectedVariantAttribute() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');
    if (!closestElement) return;

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-product-card-selected_variant_id') {
          const newValue = closestElement.getAttribute('data-product-card-selected_variant_id');
          if (newValue) this.scrollToSlide(newValue);
        }
      }
    });

    observer.observe(closestElement, { attributes: true });
  }

  handleInitialVariantSelection() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');
    if (!closestElement) {
      console.warn("No ancestor element found with 'data-product-card-selected_variant_id' attribute for initial variant selection.");
      return;
    }

    const initialVariantId = closestElement.getAttribute('data-product-card-selected_variant_id');
    if (initialVariantId) {
      this.scrollToSlide(initialVariantId);
    }
  }
  
  scrollToSlide(selectedVariantId) {
    const targetSlideIndex = this.findSlideIndexByVariantId(selectedVariantId);
    if (targetSlideIndex !== -1) {
      this.emblaApiMain.scrollTo(targetSlideIndex, false);
      if (this.emblaApiThumb) {
        this.emblaApiThumb.scrollTo(targetSlideIndex, false);
      }
    }
  }

  findSlideIndexByVariantId(variantId) {
    return [...this.emblaApiMain.slideNodes()].findIndex(slide => slide.dataset.targetVariant === variantId);
  }

  init() {
    this.emblaApiMain = EmblaCarousel(this.viewportNodeMainCarousel, this.options);

    if (this.viewportNodeThumbCarousel) {
      this.emblaApiThumb = EmblaCarousel(this.viewportNodeThumbCarousel, { ...this.options, ...this.option_thumbs });
      const removeThumbHandlers = [
        this.addThumbBtnsClickHandlers(),
        this.addToggleThumbBtnsActive(),
      ];
      this.emblaApiThumb.on('destroy', () => removeThumbHandlers.forEach(remove => remove()));
    } else if (this.thumbStripNode) {
      this.initThumbStrip();
    }

    const removeHandlers = [
      this.addPrevNextBtnsClickHandlers(this.emblaApiMain, this.elements.prevButton, this.elements.nextButton),
    ];
    this.emblaApiMain.on('destroy', () => removeHandlers.forEach(remove => remove()));
  }

  addThumbBtnsClickHandlers() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const scrollToIndex = slidesThumbs.map((_, index) => () => {
      this.emblaApiMain.scrollTo(index, false);
    });

    slidesThumbs.forEach((slideNode, index) => {
      slideNode.addEventListener('click', scrollToIndex[index], false);
    });

    return () => {
      slidesThumbs.forEach((slideNode, index) => {
        slideNode.removeEventListener('click', scrollToIndex[index], false);
      });
    };
  }

  addToggleThumbBtnsActive() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const toggleThumbBtnsState = () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      const previous = this.emblaApiMain.previousScrollSnap();
      this.emblaApiThumb.scrollTo(selected);
      slidesThumbs[previous]?.classList.remove('selected');
      slidesThumbs[selected]?.classList.add('selected');
    };

    this.emblaApiMain.on('select', toggleThumbBtnsState);
    this.emblaApiThumb.on('init', toggleThumbBtnsState);

    return () => {
      slidesThumbs.forEach(slide => slide.classList.remove('selected'));
    };
  }

  initThumbStrip() {
    const slides = [...this.thumbStripNode.querySelectorAll('[data-slide]')];
    if (!slides.length) return;

    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        this.emblaApiMain.scrollTo(index, false);
      });
    });

    const sync = () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      slides.forEach((s, i) => s.classList.toggle('selected', i === selected));
      const target = slides[selected];
      if (target) {
        const stripRect = this.thumbStripNode.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const isVertical = stripRect.height > stripRect.width;
        if (isVertical) {
          const offset = target.offsetTop - this.thumbStripNode.offsetTop - (stripRect.height - targetRect.height) / 2;
          this.thumbStripNode.scrollTo({ top: offset, behavior: 'smooth' });
        } else {
          const offset = target.offsetLeft - this.thumbStripNode.offsetLeft - (stripRect.width - targetRect.width) / 2;
          this.thumbStripNode.scrollTo({ left: offset, behavior: 'smooth' });
        }
      }
    };

    this.emblaApiMain.on('select', sync).on('init', sync);
    sync();
  }

  addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
    const scrollPrev = () => emblaApi.scrollPrev();
    const scrollNext = () => emblaApi.scrollNext();

    if (prevBtn) {
      prevBtn.addEventListener('click', scrollPrev, false);
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', scrollNext, false);
    }

    const toggleState = () => {
      if (prevBtn) prevBtn.disabled = !emblaApi.canScrollPrev();
      if (nextBtn) nextBtn.disabled = !emblaApi.canScrollNext();
    };

    emblaApi.on('select', toggleState).on('init', toggleState);

    return () => {
      if (prevBtn) prevBtn.removeEventListener('click', scrollPrev, false);
      if (nextBtn) nextBtn.removeEventListener('click', scrollNext, false);
    };
  }
}

customElements.define('product-media-gallery', ProductGallery);